/**
 * Custom Tiptap extensions used by the Letter editor.
 *
 *   - FontSize: stores arbitrary font-size on a `textStyle` mark
 *               (Tiptap doesn't ship one by default).
 *   - LineHeight: stores `line-height` as a block attribute on paragraphs +
 *                 headings so users can tighten/loosen paragraph spacing.
 *
 * Both extensions piggy-back on @tiptap/extension-text-style and expose
 * familiar chainable commands (`setFontSize`, `setLineHeight`, etc.).
 */
import { Extension } from '@tiptap/core';

export const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() {
        return { types: ['textStyle'] };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (el) => el.style.fontSize || null,
                        renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize:
                (size) =>
                ({ chain }) =>
                    chain().setMark('textStyle', { fontSize: size }).run(),
            unsetFontSize:
                () =>
                ({ chain }) =>
                    chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
        };
    },
});

export const LineHeight = Extension.create({
    name: 'lineHeight',
    addOptions() {
        return { types: ['paragraph', 'heading'], defaultLineHeight: null };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    lineHeight: {
                        default: this.options.defaultLineHeight,
                        parseHTML: (el) => el.style.lineHeight || null,
                        renderHTML: (attrs) => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setLineHeight:
                (value) =>
                ({ commands }) => {
                    return this.options.types.every((t) => commands.updateAttributes(t, { lineHeight: value }));
                },
            unsetLineHeight:
                () =>
                ({ commands }) => {
                    return this.options.types.every((t) => commands.updateAttributes(t, { lineHeight: null }));
                },
        };
    },
});
